import type { SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  ViewChild,
  output,
} from '@angular/core';
import { UntypedFormControl, ReactiveFormsModule } from '@angular/forms';

import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { TimePickerDropdownComponent } from '@core/time-ui';
import {
  ConnectedOverlayDirective,
  KeyCode,
  InputComponent,
  OverlayOriginDirective,
} from '@core/user-interface-legacy';

@Component({
  selector: 'rt-schedule-duration-picker',
  templateUrl: 'schedule-duration-picker.component.html',
  styleUrls: ['./schedule-duration-picker.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    OverlayOriginDirective,
    InputComponent,
    ReactiveFormsModule,
    ConnectedOverlayDirective,
    TimePickerDropdownComponent,
    TranslatePipe,
  ],
})
export class ScheduleDurationPickerComponent {
  public readonly dropdownWidth: number = 120;

  @Input() public dropdownMaxHeight: number = 300;

  @Input() public underlined: boolean = false;
  @Input() public duration: string | number | null = null;
  @Input() public noLeftPadding: boolean = false;
  @Input() public invalid: boolean = false;

  public durationChange = output<string>();

  public durationControl: UntypedFormControl;
  public currentDuration: string = '';
  public changedManually: boolean = false;

  @ViewChild('durationDropdown', { static: true })
  private durationDropdown: ConnectedOverlayDirective;

  constructor(
    private i18n: I18nService,
    private cdRef: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    if (this.duration !== null) {
      this.currentDuration = this.duration.toString();
    }

    this.durationControl = new UntypedFormControl(this.currentDuration);
    this.cdRef.detectChanges();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const { duration } = changes;

    if (duration && duration.currentValue && this.durationControl) {
      this.currentDuration = duration.currentValue.toString();
      this.durationControl.setValue(this.currentDuration, { emitEvent: false });
    }
  }

  public onInputChange(value: string): void {
    this.changedManually = true;
    this.currentDuration = value;
    this.cdRef.detectChanges();
    this.durationChange.emit(this.currentDuration);
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (this.changedManually && event.keyCode === KeyCode.Enter) {
      event.preventDefault();
      event.stopPropagation();
      this.closeDropdown(this.durationDropdown);
    }

    if (
      this.changedManually &&
      (event.keyCode === KeyCode.DownArrow || event.keyCode === KeyCode.UpArrow)
    ) {
      this.changedManually = false;
      this.cdRef.detectChanges();
    }
  }

  public onDurationChange(duration: string): void {
    const replaceSearchValue = ' ' + this.i18n.translate('app.interview.schedule_dialog.minutes');
    const time = (duration || '').replace(replaceSearchValue, '');

    this.durationControl.setValue(time);
    this.currentDuration = time;
    this.cdRef.detectChanges();
    this.durationChange.emit(time);
  }

  public toggleDropdown(dropdown: ConnectedOverlayDirective): void {
    dropdown.open = !dropdown.open;
    this.cdRef.detectChanges();
  }

  public closeDropdown(dropdown: ConnectedOverlayDirective): void {
    dropdown.open = false;
    this.cdRef.detectChanges();
  }
}
